import React from 'react';
import { connect } from 'react-redux';
import { compose, lifecycle, withHandlers, withState } from 'recompose';
import { Switch, Route } from 'react-router-dom';
import MessengerAdsTable from '../../components/MessengerAdsTable/MessengerAdsTable';
import MessengerAdDetail from '../../components/MessengerAdDetail/MessengerAdDetail';
import NewMessengerAd from '../../components/NewMessengerAd/NewMessengerAd';
import NewEntityButton from '../../../../components/NewEntityButton/NewEntityButton';
import MessengerAdCRUDActions from '@mm/state/actions/MessengerAdCRUDActions';
import { unselectMessengerAd } from '@mm/state/actions/viewActions';
import * as routes from '../../../../../common/constants/routes';
import { ADD_ENTITY_TEXT, DEFAULT_ENTITY_NAME } from '../../constants/texts';
import { RouterComponentProps } from '../../../../../common/helpers/routes/interfaces';
import '../../../common/css/sharedPanel.scss';
import { StoreState } from '@mm/state/store-interfaces';
import { Dispatch } from 'redux';
import { LifeCycleThis } from '@mm/utils';
import { welcomePageSelector } from '@mm/state/selectors/onboardingSelectors';
import audiencesCRUDActions from '@mm/state/actions/AudiencesCRUDActions';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';

type OuterProps = {};
type PropsFromWithState = {
  requestedMessengerAd: boolean;
  setRequestedMessengerAd: (reqeusted: boolean) => void;
};
type PropsFromWithHandlers = {
  renderNewEntityButton: ReturnType<typeof renderNewEntityButton>;
};
type PropsFromState = ReturnType<typeof mapStateToProps>;
type PropsFromDispatch = ReturnType<typeof mapDispatchToProps>;
type InnerProps = RouterComponentProps &
  PropsFromWithState &
  PropsFromState &
  PropsFromDispatch &
  PropsFromWithHandlers;
type Props = InnerProps & OuterProps;

const MessengerAdsPanel = ({
  chatbotId,
  match,
  createAd,
  activePageId,
}: Props) => {
  return (
    <div className="shared-panel">
      <Switch>
        <Route
          exact
          path={match.url}
          render={() => {
            const entity = {
              name: DEFAULT_ENTITY_NAME,
              optinPageEnabled: true,
              pageId: activePageId,
            };

            return (
              <MessengerAdsTable createAd={() => createAd(chatbotId, entity)} />
            );
          }}
        />
        <Route
          path={`${match.path}/edit/:messengerAdId`}
          component={MessengerAdDetail}
        />
        <Route
          path={`${match.path}/new`}
          render={() => {
            const entity = {
              optinPageEnabled: true,
              pageId: activePageId,
            };

            return (
              <NewMessengerAd
                createMessengerAd={() => createAd(chatbotId, entity)}
                botId={chatbotId}
              />
            );
          }}
        />
      </Switch>
    </div>
  );
};

const renderNewEntityButton =
  ({ createAd, entities }) =>
  () => {
    if (entities && entities.length > 0) {
      return (
        <NewEntityButton
          text={ADD_ENTITY_TEXT}
          name={DEFAULT_ENTITY_NAME}
          createEntity={createAd}
          extraFields={{ optinPageEnabled: true }}
        />
      );
    }

    return null;
  };

const mapStateToProps = (state: StoreState) => ({
  entities: state.messengerAds.entities,
  ad: state.view.chatbotEditor.selectedMessengerAd,
  chatbotId: activeChatbotIdSelector(state),
  activePageId: (welcomePageSelector(state) || state.page.entities[0]).id,
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>, ownProps) => ({
  createAd: async (botId: number, entity) => {
    dispatch(unselectMessengerAd());
    await dispatch(MessengerAdCRUDActions.create(botId, entity));
    dispatch(audiencesCRUDActions.retrieveAll(botId));
    ownProps.setRequestedMessengerAd(true);
  },
});

function componentDidUpdate(this: LifeCycleThis<Props>) {
  const {
    ad,
    history,
    match: {
      params: { chatbotId },
      isExact,
    },
    requestedMessengerAd,
    setRequestedMessengerAd,
  } = this.props;

  if (requestedMessengerAd && isExact && ad) {
    history.push(
      `${routes.CHATBOT_EDITOR}/${chatbotId}/messenger-ads/edit/${ad.id}`,
    );
    setRequestedMessengerAd(false);
  }
}

const enhance = compose<InnerProps, OuterProps>(
  withState('requestedMessengerAd', 'setRequestedMessengerAd', false),
  connect(mapStateToProps),
  connect(null, mapDispatchToProps),
  lifecycle<Props, {}>({ componentDidUpdate }),
  withHandlers({ renderNewEntityButton }),
);

export default enhance(MessengerAdsPanel);
